// ═══════════════════════════════════════════════════════════════
// TemplateVariablesEditor.tsx – a szervezet sablonváltozóinak ({{KULCS}}) szerkesztője
//
// - a mezőlista a backend katalógusából jön (beépített + a szervezet saját változói)
// - jelzi, mi mentett és mi jön a törzsadatból; egy kattintással visszaállítható
// - saját változó felvétele / törlése
// - szervezet_valtozok.json betöltése (a mezőkbe tölt, mentés előtt ellenőrizhető)
// ═══════════════════════════════════════════════════════════════
import React, { useMemo, useRef, useState } from 'react';
import type { TemplateVariableDefinition } from '../../types';
import { PhoneInput } from '../common/PhoneInput';
import { validatePhone } from '../../utils/phone';
import { FileJson, Plus, RotateCcw, Save, Trash2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';

export interface CustomVariable { key: string; label: string }

const GROUPS: { id: string; title: string }[] = [
  { id: 'szervezet', title: 'Szervezeti alapadatok' },
  { id: 'szemelyzet', title: 'Tisztségviselők' },
  { id: 'felugyelet', title: 'Felügyelet' },
  { id: 'digitalis', title: 'Digitális azonosítók' },
  { id: 'penzugy', title: 'Pénzügyi azonosítók' },
  { id: 'egyedi', title: 'Saját változók' },
];
const REQUIRED = new Set(['ONKORMANYZAT_NEVE', 'HIVATAL_NEVE']);
/** telefonszám-mezők: azonnali formátum-ellenőrzés (a szerver is ellenőrzi) */
export const PHONE_VAR_KEYS = new Set(['SZERVEZET_TELEFON', 'SZERVEZET_FAX', 'TELEFON']);
const MONO = new Set(['HIVATALI_KAPU_PH', 'HIVATALI_KAPU_ONK', 'KRID_PH', 'KRID_ONK', 'PIR_ID', 'ILLETEK_SZAMLASZAM']);
const WIDE = new Set(['ONKORMANYZAT_NEVE', 'HIVATAL_NEVE', 'UGYFELFORGALMI_REND', 'SZERVEZET_CIME', 'SZERVEZET_POSTACIM']);
const KEY_RE = /^[A-Z][A-Z0-9_]{1,49}$/;
export const META_TENANT_KEY = '_szervezet_azonosito';

/** megnevezésből javasolt kulcs: „Ügyfélkapcsolati vezető” → UGYFELKAPCSOLATI_VEZETO */
export const keyFromLabel = (label: string) =>
  label.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^[0-9_]+/, '').slice(0, 50);

interface Props {
  definitions: TemplateVariableDefinition[];
  customVars: CustomVariable[];
  onCustomVarsChange: (c: CustomVariable[]) => void;
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onBulkChange: (patch: Record<string, string>) => void;
  /** az utoljára betöltött / mentett állapot (változás-jelzéshez) */
  baseline: Record<string, string>;
  customBaseline: CustomVariable[];
  derived: Record<string, string>;
  savedKeys: string[];
  tenantSlug?: string;
  canSave: boolean;
  saving: boolean;
  onSave: () => void;
  savedNotice?: string | null;
}

export const TemplateVariablesEditor: React.FC<Props> = ({
  definitions, customVars, onCustomVarsChange, values, onChange, onBulkChange,
  baseline, customBaseline, derived, savedKeys, tenantSlug, canSave, saving, onSave, savedNotice,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newKey, setNewKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [jsonInfo, setJsonInfo] = useState<{ changed: string[]; unknown: string[]; foreign: string | null; pending?: Record<string, string> } | null>(null);
  const [highlight, setHighlight] = useState<Set<string>>(new Set());

  const catalog = useMemo(() => {
    const builtin = definitions.filter((d) => !d.custom && !d.computed);
    return [...builtin, ...customVars.map((c) => ({ key: c.key, label: c.label, category: 'egyedi', custom: true } as TemplateVariableDefinition))];
  }, [definitions, customVars]);
  const computed = definitions.filter((d) => d.computed);
  const savedSet = useMemo(() => new Set(savedKeys), [savedKeys]);
  // hibás (és a mentetthez képest megváltozott) telefonszám – a mentés addig nem indítható
  const badPhones = catalog.filter((d) => PHONE_VAR_KEYS.has(d.key) && (values[d.key] ?? '') !== (baseline[d.key] ?? '') && !validatePhone(values[d.key] ?? '').ok);
  const dirtyCount = catalog.filter((d) => (values[d.key] ?? '') !== (baseline[d.key] ?? '')).length
    + (JSON.stringify(customVars) !== JSON.stringify(customBaseline) ? 1 : 0);

  const addCustom = () => {
    const label = newLabel.trim();
    const key = (newKey || keyFromLabel(label)).trim().toUpperCase();
    if (!label) return setAddError('Add meg a megnevezést');
    if (!KEY_RE.test(key)) return setAddError('A kulcs nagybetűvel kezdődjön; csak A–Z, 0–9 és _ (2–50 karakter)');
    if (key.startsWith('BLOKK_')) return setAddError('A BLOKK_ előtag a beépített szövegblokkoké');
    if (catalog.some((d) => d.key === key) || computed.some((d) => d.key === key)) return setAddError(`Már van ${key} nevű változó`);
    onCustomVarsChange([...customVars, { key, label }]);
    onChange(key, '');
    setAdding(false); setNewLabel(''); setNewKey(''); setKeyTouched(false); setAddError(null);
  };

  const applyJson = (src: Record<string, unknown>) => {
    const known = new Set(catalog.map((d) => d.key));
    const patch: Record<string, string> = {};
    const unknown: string[] = [];
    for (const [k, raw] of Object.entries(src)) {
      if (k.startsWith('_') || (typeof raw !== 'string' && typeof raw !== 'number')) continue;
      if (computed.some((d) => d.key === k)) continue;
      if (known.has(k)) { if (String(raw) !== (values[k] ?? '')) patch[k] = String(raw); }
      else if (KEY_RE.test(k) && !k.startsWith('BLOKK_') && !(k in derived)) unknown.push(k);
    }
    onBulkChange(patch);
    setHighlight(new Set(Object.keys(patch)));
    setJsonInfo({ changed: Object.keys(patch), unknown, foreign: null, pending: Object.fromEntries(unknown.map((k) => [k, String(src[k])])) });
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    let parsed: unknown;
    try { parsed = JSON.parse(await f.text()); } catch {
      alert('A fájl nem érvényes JSON.');
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) { alert('A JSON-nak KULCS: "érték" párokat kell tartalmaznia.'); return; }
    const src = parsed as Record<string, unknown>;
    const meta = typeof src[META_TENANT_KEY] === 'string' ? (src[META_TENANT_KEY] as string) : '';
    if (meta && tenantSlug && meta !== tenantSlug) {
      // másik szervezet exportja – csak megerősítés után töltjük be
      setJsonInfo({ changed: [], unknown: [], foreign: meta, pending: src as Record<string, string> });
      return;
    }
    applyJson(src);
  };

  const adoptUnknown = () => {
    if (!jsonInfo?.pending) return;
    const add = jsonInfo.unknown.filter((k) => !catalog.some((d) => d.key === k)).map((k) => ({ key: k, label: k }));
    onCustomVarsChange([...customVars, ...add]);
    onBulkChange(Object.fromEntries(add.map((a) => [a.key, jsonInfo.pending![a.key] ?? ''])));
    setHighlight(new Set([...highlight, ...add.map((a) => a.key)]));
    setJsonInfo({ ...jsonInfo, changed: [...jsonInfo.changed, ...add.map((a) => a.key)], unknown: [] });
  };

  const field = (d: TemplateVariableDefinition) => {
    const v = values[d.key] ?? '';
    const isSaved = savedSet.has(d.key) && !d.custom;
    const differsFromDerived = !d.custom && v !== (derived[d.key] ?? '');
    const dirty = v !== (baseline[d.key] ?? '');
    const id = `tplvar-${d.key}`;
    return (
      <div key={d.key} className={WIDE.has(d.key) ? 'sm:col-span-2' : ''}>
        <div className="flex items-center justify-between gap-2 mb-1">
          <label htmlFor={id} className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate" title={d.label}>
            {d.label}{REQUIRED.has(d.key) ? ' *' : ''}
          </label>
          <span className="flex items-center gap-1 shrink-0">
            {dirty ? (
              <span className="text-[10px] px-1.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">módosítva</span>
            ) : isSaved ? (
              <span className="text-[10px] px-1.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300" title="A szervezetnél mentett érték (felülírja a törzsadatot)">mentett</span>
            ) : null}
            {differsFromDerived && (derived[d.key] ?? '') !== '' && (
              <button type="button" onClick={() => onChange(d.key, derived[d.key] ?? '')}
                className="p-0.5 rounded text-slate-400 hover:text-indigo-600 cursor-pointer"
                title={`Vissza a törzsadatra: ${derived[d.key]}`} aria-label={`${d.label}: vissza a törzsadatra`}>
                <RotateCcw className="w-3 h-3" />
              </button>
            )}
            {d.custom && (
              <button type="button" onClick={() => { onCustomVarsChange(customVars.filter((c) => c.key !== d.key)); }}
                className="p-0.5 rounded text-slate-400 hover:text-rose-600 cursor-pointer" title="Saját változó törlése" aria-label={`${d.label} törlése`}>
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </span>
        </div>
        {PHONE_VAR_KEYS.has(d.key) ? (
          <PhoneInput
            id={id}
            value={v}
            onChange={(nv) => onChange(d.key, nv)}
            placeholder={d.example ? `pl. ${d.example}` : undefined}
            className={`w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border rounded-xl text-xs text-slate-900 dark:text-white ${
              highlight.has(d.key) ? 'border-amber-400 ring-1 ring-amber-300 dark:border-amber-600' : 'border-slate-200 dark:border-slate-800'}`}
          />
        ) : (
        <input
          id={id}
          type="text"
          required={REQUIRED.has(d.key)}
          value={v}
          onChange={(e) => onChange(d.key, e.target.value)}
          placeholder={d.example ? `pl. ${d.example}` : ''}
          className={`w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border rounded-xl text-xs text-slate-900 dark:text-white ${MONO.has(d.key) ? 'font-mono' : ''} ${
            highlight.has(d.key) ? 'border-amber-400 ring-1 ring-amber-300 dark:border-amber-600' : 'border-slate-200 dark:border-slate-800'}`}
        />
        )}
        <div className="text-[10px] text-slate-400 font-mono mt-0.5 select-all" title="Így hivatkozhatsz rá a leírásokban (leiras.txt)">{`{{${d.key}}}`}</div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Szervezeti adatok és helyettesítők</h4>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={handleFile} />
          <button type="button" onClick={() => fileRef.current?.click()}
            className="px-2.5 py-1 text-[11px] font-semibold rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-1 cursor-pointer"
            title="szervezet_valtozok.json betöltése a mezőkbe (mentés előtt ellenőrizheted)">
            <FileJson className="w-3.5 h-3.5" /> JSON betöltése
          </button>
          {canSave && (
            <button type="button" onClick={onSave} disabled={saving || badPhones.length > 0}
              className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1 cursor-pointer disabled:opacity-50"
              title="A változók mentése ennél a szervezetnél – legközelebb ezek töltődnek be, és a letöltött ZIP json-ja is ezeket tartalmazza">
              {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Változók mentése{dirtyCount ? ` (${dirtyCount})` : ''}
            </button>
          )}
        </div>
      </div>
      {badPhones.length > 0 && (
        <p role="alert" className="text-[11px] text-rose-600 dark:text-rose-400">
          Hibás telefonszám: {badPhones.map((d) => d.label).join(', ')} – javítsd a mentés előtt.
        </p>
      )}
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        Az üres mezők a szervezet törzsadataiból (Szervezetek, Kapcsolt szervezetek) töltődnek. A „mentett” értékek felülírják a törzsadatot; a <RotateCcw className="inline w-3 h-3" /> visszaállítja.
        {!canSave && ' A változók tartós mentéséhez szervezeti admin jogosultság kell – itt csak ehhez a telepítéshez módosulnak.'}
      </p>

      {savedNotice && (
        <div className="p-2 rounded-lg text-[11px] bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900 flex items-center gap-1.5" role="status">
          <CheckCircle2 className="w-3.5 h-3.5" /> {savedNotice}
        </div>
      )}

      {jsonInfo && (
        <div className="p-2.5 rounded-lg text-[11px] border bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-200 space-y-1.5" role="status">
          {jsonInfo.foreign ? (
            <>
              <div className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>A fájl egy másik szervezet exportja ({jsonInfo.foreign}). Ha betöltöd, az ő adatai kerülnek a mezőkbe.</span></div>
              <div className="flex gap-2">
                <button type="button" className="px-2 py-0.5 rounded bg-amber-600 text-white cursor-pointer" onClick={() => applyJson(jsonInfo.pending || {})}>Betöltöm mégis</button>
                <button type="button" className="px-2 py-0.5 rounded border border-amber-300 cursor-pointer" onClick={() => setJsonInfo(null)}>Mégse</button>
              </div>
            </>
          ) : (
            <>
              <div>
                {jsonInfo.changed.length
                  ? `${jsonInfo.changed.length} mező módosult a JSON alapján (sárgával jelölve) – ellenőrizd, majd ${canSave ? 'mentsd' : 'telepítsd'}.`
                  : 'A JSON értékei megegyeznek a mostaniakkal.'}
              </div>
              {jsonInfo.unknown.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span>Ismeretlen kulcsok: <span className="font-mono">{jsonInfo.unknown.join(', ')}</span></span>
                  <button type="button" className="px-2 py-0.5 rounded bg-indigo-600 text-white cursor-pointer" onClick={adoptUnknown}>Felvétel saját változóként</button>
                </div>
              )}
              <button type="button" className="underline cursor-pointer" onClick={() => { setJsonInfo(null); setHighlight(new Set()); }}>Bezár</button>
            </>
          )}
        </div>
      )}

      {GROUPS.map((g) => {
        const items = catalog.filter((d) => (d.category || 'szervezet') === g.id || (g.id === 'szervezet' && !GROUPS.some((x) => x.id === d.category)));
        if (!items.length && g.id !== 'egyedi') return null;
        return (
          <fieldset key={g.id} className="space-y-2">
            <legend className="text-[11px] font-bold text-slate-600 dark:text-slate-300 mb-1">{g.title}</legend>
            {items.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{items.map(field)}</div>}
            {g.id === 'egyedi' && (
              adding ? (
                <div className="p-2.5 rounded-xl border border-indigo-200 dark:border-indigo-900 bg-indigo-50/50 dark:bg-indigo-950/20 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label htmlFor="tplvar-new-label" className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-0.5">Megnevezés</label>
                      <input id="tplvar-new-label" autoFocus value={newLabel}
                        onChange={(e) => { setNewLabel(e.target.value); if (!keyTouched) setNewKey(keyFromLabel(e.target.value)); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
                        placeholder="pl. Ügyfélkapcsolati vezető neve"
                        className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-xs" />
                    </div>
                    <div>
                      <label htmlFor="tplvar-new-key" className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-0.5">Kulcs (a leírásban: {'{{KULCS}}'})</label>
                      <input id="tplvar-new-key" value={newKey}
                        onChange={(e) => { setKeyTouched(true); setNewKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '')); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
                        placeholder="UGYFELKAPCSOLATI_VEZETO"
                        className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-mono" />
                    </div>
                  </div>
                  {addError && <div className="text-[11px] text-rose-700 dark:text-rose-300" role="alert">{addError}</div>}
                  <div className="flex gap-2">
                    <button type="button" onClick={addCustom} className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-indigo-600 text-white cursor-pointer">Hozzáadás</button>
                    <button type="button" onClick={() => { setAdding(false); setAddError(null); }} className="px-2.5 py-1 text-[11px] rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">Mégse</button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => setAdding(true)}
                  className="px-2.5 py-1 text-[11px] font-semibold rounded-lg border border-dashed border-indigo-300 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 flex items-center gap-1 cursor-pointer">
                  <Plus className="w-3.5 h-3.5" /> Saját változó hozzáadása
                </button>
              )
            )}
          </fieldset>
        );
      })}
      {computed.length > 0 && (
        <p className="text-[10px] text-slate-400">
          Automatikus: {computed.map((d) => <span key={d.key} className="font-mono mr-2" title={d.label}>{`{{${d.key}}}`}</span>)}
        </p>
      )}
    </div>
  );
};
